import React, { useEffect, useRef, useState } from 'react';
import api from '../api';
import RunsPicture, { fmtDay, fmtSpan, MovedList } from './RunsPicture';

// Supplied from stock, with what it does to the board (the director, 15 Oct
// 2026). From the order page the board is out of sight, so before saving the
// pop-up draws what the figure does there: the line's days that come off, the
// run that gets shorter, and what bringing the runs behind forward would move.
// He chooses bring forward or leave them where they are; the save does exactly
// what was drawn, or -- if the board changed meanwhile -- nothing, and draws
// it again. Made and from stock together count toward the line being done.

const TEAL   = '#14b8a6';
const BLUE   = '#93c5fd';
const AMBER  = '#f59e0b';
const RED    = '#ef4444';
const SUNK   = '#12161c';
const BORDER = '#2a3040';
const MUTED  = '#6b7280';
const TEXT   = '#e5e7eb';
const DIM    = '#9ca3af';

const nf = (n) => Number(n || 0).toLocaleString('en-GB');
const daysWord = (n) => `${n} day${n === 1 ? '' : 's'}`;

// line: an order line as the order list has it (id, quantity, from_stock_qty,
// part, product name); startQty: the figure to start from.
export default function StockModal({ line, title, startQty, toast, onClose, onSaved, onShowBoard }) {
  const [qty, setQty] = useState(String(startQty ?? ''));
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [choices, setChoices] = useState({});   // machine_id -> 'forward' | 'leave'
  const [saving, setSaving] = useState(false);
  const asked = useRef(0);

  // Ask again as the figure changes, a moment after typing stops.
  useEffect(() => {
    const n = ++asked.current;
    setLoading(true);
    const t = setTimeout(() => {
      api.post(`/api/v1/orders/lines/${line.id}/stock/preview`, { from_stock_qty: qty === '' ? null : Number(qty) })
        .then(r => { if (n === asked.current) { setPlan(r.data); setError(null); } })
        .catch(err => { if (n === asked.current) { setPlan(null); setError(err.response?.data?.error || 'Could not work out what this does to the board.'); } })
        .finally(() => { if (n === asked.current) setLoading(false); });
    }, 300);
    return () => clearTimeout(t);
  }, [qty, line.id]);

  useEffect(() => {
    const esc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [onClose]);

  const lanes = plan?.lanes || [];
  const toChoose = lanes.filter(l => l.forward);
  const unchosen = toChoose.filter(l => !choices[l.machine_id]);
  const canSave = !!plan && !loading && !saving && !error && unchosen.length === 0;

  function save() {
    if (!canSave) return;
    setSaving(true);
    const forward = toChoose.some(l => choices[l.machine_id] === 'forward');
    api.post(`/api/v1/orders/lines/${line.id}/stock`, {
      from_stock_qty: qty === '' ? null : Number(qty),
      bring_forward: forward,
      expect_sig: plan.sig,
    })
      .then(r => {
        const d = r.data || {};
        const bits = [];
        if (d.days_off) bits.push(`${daysWord(d.days_off)} taken off the board`);
        if (d.cut_down?.length) bits.push(`${fmtDay(d.cut_down[0].date)} cut to ${nf(d.cut_down[0].to)} pcs`);
        if (d.moved) bits.push(`${d.moved} run${d.moved === 1 ? '' : 's'} brought forward`);
        toast.success(`Saved — ${qty ? `${nf(qty)} pcs from stock` : 'no longer from stock'}${bits.length ? `; ${bits.join(', ')}` : ''}.`, 7000);
        for (const why of d.refused || []) toast.error(why, 10000);
        if (d.order_completed) toast.success(`${d.order_completed} completed — every line is done. It is in History; Reopen it there if that was too soon.`, 8000);
        onSaved(d);
      })
      .catch(err => {
        const body = err.response?.data;
        if (err.response?.status === 409 && body?.plan) { setPlan(body.plan); setChoices({}); }
        setError(body?.error || 'Could not save.');
        setSaving(false);
      });
  }

  const p = plan;
  const ordered = p?.line?.quantity ?? line.quantity ?? 0;
  const made = p?.line?.made ?? 0;
  const stockNow = qty === '' ? 0 : Number(qty) || 0;

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300, padding: 16 }}>
      <div onClick={e => e.stopPropagation()} role="dialog" aria-label="Supplied from stock"
        style={{ background: '#1a2030', border: `1px solid ${BORDER}`, borderRadius: 16, width: '100%', maxWidth: 780, maxHeight: '92vh', overflowY: 'auto', fontFamily: 'system-ui' }}>
        <div style={{ padding: '18px 20px 14px', borderBottom: `1px solid ${BORDER}`, display: 'flex', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#f0f2f5' }}>Supplied from stock</div>
            <div style={{ fontSize: 12, color: MUTED, marginTop: 2 }}>{title}</div>
          </div>
          <button onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', color: MUTED, cursor: 'pointer', fontSize: 16 }}>✕</button>
        </div>

        <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* The figures: made and from stock add up to the line being done. */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'stretch' }}>
            <Figure label="Ordered" value={nf(ordered)} />
            <Figure label="Made" value={made ? nf(made) : '—'} />
            <div style={{ background: SUNK, border: `1px solid ${BLUE}55`, borderRadius: 10, padding: '8px 12px', minWidth: 150 }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: BLUE }}>From stock</div>
              <input type="number" min="0" max={ordered || undefined} value={qty} autoFocus
                onChange={e => setQty(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') save(); }}
                aria-label="Pieces supplied from stock"
                style={{ width: '100%', boxSizing: 'border-box', marginTop: 2, background: '#0f1117', border: `1px solid ${BLUE}88`, borderRadius: 6,
                         color: TEXT, fontSize: 18, fontWeight: 700, padding: '3px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }} />
            </div>
            <Figure label="Still to make" value={p ? nf(p.need) : '…'}
              tone={p && p.need === 0 ? '#4ade80' : TEXT}
              note={p && made + stockNow >= ordered && ordered ? 'made + stock cover it' : null} />
          </div>

          {/* What it does to the board. */}
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: MUTED, marginBottom: 8 }}>
              On the board {loading && <span style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 400 }}>· working it out…</span>}
            </div>
            {error && (
              <div style={{ background: `${RED}14`, border: `1px solid ${RED}55`, color: '#fca5a5', borderRadius: 8, padding: '8px 12px', fontSize: 12.5, marginBottom: 10 }}>
                {error}
              </div>
            )}
            {p && !lanes.length && <Quiet plan={p} />}
            {p && lanes.map(l => (
              <div key={l.machine_id} style={{ background: SUNK, border: `1px solid ${BORDER}`, borderRadius: 12, padding: '12px 14px', marginBottom: 10 }}>
                <div style={{ fontSize: 13, color: TEXT, marginBottom: 10 }}>
                  <strong>{l.machine}</strong>
                  <span style={{ color: DIM }}> · {whatHappens(p, l)}</span>
                </div>
                <RunsPicture lane={l} choice={choices[l.machine_id] || 'leave'} />
                {l.forward ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 12, fontSize: 13, color: '#d1d5db' }}>
                    <Choice checked={choices[l.machine_id] === 'forward'} onChange={() => setChoices(c => ({ ...c, [l.machine_id]: 'forward' }))}
                      label="Bring forward" sub={<MovedList lane={l} />} tone={AMBER} />
                    <Choice checked={choices[l.machine_id] === 'leave'} onChange={() => setChoices(c => ({ ...c, [l.machine_id]: 'leave' }))}
                      label="Leave them where they are" sub={l.gap ? `${l.machine} stays free ${fmtSpan(l.gap.from, l.gap.to)}.` : 'Nothing else moves.'} tone={TEAL} />
                  </div>
                ) : (
                  <div style={{ fontSize: 12, color: MUTED, marginTop: 10 }}>
                    {l.gap ? `Nothing sits directly behind it, so nothing moves up — ${l.machine} is simply free ${fmtSpan(l.gap.from, l.gap.to)}.` : 'Nothing else on this machine changes.'}
                  </div>
                )}
              </div>
            ))}
            {p?.short > 0 && (
              <div style={{ fontSize: 12.5, color: AMBER, marginTop: 4 }}>
                {nf(p.short)} pcs still to make are not on the board. After saving, plan them from the order with Schedule.
              </div>
            )}
          </div>
        </div>

        <div style={{ padding: '0 20px 18px', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {onShowBoard && (
            <button onClick={onShowBoard} style={{ background: 'none', border: 'none', color: TEAL, cursor: 'pointer', fontSize: 12.5, padding: 0 }}>
              <i className="fa-solid fa-table-cells" style={{ marginRight: 6 }} />Show on the board
            </button>
          )}
          <span style={{ flex: 1 }} />
          {unchosen.length > 0 && !loading && <span style={{ fontSize: 12, color: AMBER }}>Choose: bring forward, or leave them where they are</span>}
          <button onClick={onClose} style={{ background: 'none', border: '1px solid #374151', borderRadius: 8, color: DIM, padding: '8px 18px', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
          <button onClick={save} disabled={!canSave}
            style={{ background: canSave ? `${BLUE}33` : `${BLUE}14`, border: `1px solid ${BLUE}88`, borderRadius: 8, color: canSave ? BLUE : `${BLUE}88`,
                     padding: '8px 22px', cursor: canSave ? 'pointer' : 'default', fontSize: 13, fontWeight: 600 }}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// "Comes off: Tue 27 – Thu 29 Oct (3 days, 22,400 pcs)", or what is cut.
function whatHappens(p, lane) {
  const off = p.remove.filter(r => r.machine_id === lane.machine_id).map(r => r.date).sort();
  const cut = p.reduce.filter(r => r.machine_id === lane.machine_id);
  const offPcs = p.remove.filter(r => r.machine_id === lane.machine_id).reduce((n, r) => n + r.qty, 0);
  const parts = [];
  const days = [...new Set(off)];
  if (days.length) parts.push(`${days.length === 1 ? fmtDay(days[0]) : fmtSpan(days[0], days[days.length - 1])} ${days.length === 1 ? 'comes' : 'come'} off (${daysWord(days.length)}, ${nf(offPcs)} pcs)`);
  for (const c of cut) parts.push(`${fmtDay(c.date)} is cut from ${nf(c.from)} to ${nf(c.to)} pcs`);
  return parts.join('; ') || 'Nothing changes here';
}

// When the board has nothing to change, say why.
function Quiet({ plan }) {
  const say = plan.planned === 0 && !plan.running
    ? 'Nothing of this line is planned on the board, so nothing there changes.'
    : plan.short > 0
      ? 'The board plans less than is still missing, so nothing comes off.'
      : 'The board already plans exactly what is still missing, so nothing there changes.';
  return <div style={{ fontSize: 12.5, color: DIM }}>{say}</div>;
}

function Figure({ label, value, tone = TEXT, note }) {
  return (
    <div style={{ background: SUNK, border: `1px solid ${BORDER}`, borderRadius: 10, padding: '8px 12px', minWidth: 110 }}>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: MUTED }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: tone, marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      {note && <div style={{ fontSize: 10.5, color: MUTED }}>{note}</div>}
    </div>
  );
}

function Choice({ checked, onChange, label, sub, tone }) {
  return (
    <label style={{ display: 'flex', gap: 9, alignItems: 'flex-start', cursor: 'pointer', padding: '7px 10px', borderRadius: 8,
                    border: `1px solid ${checked ? `${tone}88` : BORDER}`, background: checked ? `${tone}12` : 'transparent' }}>
      <input type="radio" checked={checked} onChange={onChange} style={{ marginTop: 3 }} />
      <span>{label}<span style={{ display: 'block', fontSize: 11.5, color: MUTED, marginTop: 1 }}>{sub}</span></span>
    </label>
  );
}
